import Vue, { CreateElement } from '@src/vue'
import camelcaseKeys from 'camelcase-keys'
import { Component } from 'vue-property-decorator'
import { HealthLimitationSet } from '@src/data/health-limitation-set'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { Radio } from '@src/elements/input/radio'
import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import { Checkbox } from '@src/elements/input/checkbox'
import { HealthLimitationGroupsTitles } from '@src/util/constants'
import { track } from '@src/util/functions'
import { ContentBox } from '@src/elements/content-box/content-box'
import { Textarea } from '@src/elements/input/textarea'
import { OptionGroup } from '@src/elements/select/option-group'
import { HealthLimitationService } from '@src/service/health-limitation'
import { Loader } from '@src/components/loading-screen/loader/loader'

import './health-limitations.sass'

type LimitationGroup = {
  title: string,
  limitations: any[],
  multipleSelection: boolean,  // if is allowed multiple values for group - checkboxes
  selectedValue: string   // if is allowed only one value for group - radio buttons
}
type CheckboxValue = { key: string, isSelected: boolean }

@Component
export class HealthLimitations extends Vue {

  protected healthLimitationsSet: HealthLimitationSet = new HealthLimitationSet()
  // will be true when at least one edit operation is done
  protected isUpdated: boolean = false
  protected loading = true

  private limitationGroups: LimitationGroup[] = []
  private diabetes: string | null = ''
  private diseases: CheckboxValue[] = []
  private asthma: string = ''
  private medication: string = ''
  private operations: string = ''
  private pregnancy: CheckboxValue[] = []
  private notes: string = ''
  private waterConsumption: string | null = null
  private mealFrequency: string | null = null
  private behaviourInfo: string | null = null

  constructor () {
    super()
    track('/members/health')
  }

  beforeMount () {
    return this.load()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.HEALTH')}`
  }

  assignHealthLimResponseToData () {
    this.diabetes = this.healthLimitationsSet ? this.healthLimitationsSet.diabetes : null
    this.diseases = [
      { key: 'rheumatism', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.rheumatism : false },
      { key: 'cancer', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.cancer : false },
      { key: 'osteoporosis', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.osteoporosis : false },
      { key: 'obesity', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.obesity : false },
      { key: 'varices', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.varices : false },
      { key: 'hyperthyroidism', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.hyperthyroidism : false },
      { key: 'hypothyroidism', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.hypothyroidism : false }
    ]
    this.asthma = this.healthLimitationsSet.asthmaBronchiale ? 'asthmaBronchiale' :
      this.healthLimitationsSet.asthmaAllergic ? 'asthmaAllergic' :
        this.healthLimitationsSet.asthmaExertional ? 'asthmaExertional' : ''
    this.medication = this.healthLimitationsSet ? this.healthLimitationsSet.medication : ''
    this.operations = this.healthLimitationsSet ? this.healthLimitationsSet.operations : ''
    this.pregnancy = [
      { key: 'gaveBirthLast6Months', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.gaveBirthLast6Months : false },
      { key: 'pregnant', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.pregnant : false }
    ]
    this.notes = this.healthLimitationsSet ? this.healthLimitationsSet.notes : ''

    // limitation groups
    let limitationGroups: LimitationGroup[] = []
    const limGroup1 = [
      { key: 'shoulder', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.shoulder : false },
      { key: 'knee', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.knee : false },
      { key: 'hip', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.hip : false },
      { key: 'feetAnkle', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.feetAnkle : false },
      { key: 'elbow', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.elbow : false },
      { key: 'handWrist', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.handWrist : false }
    ]
    const limGroup2 = [
      { key: 'cervicalSpine', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.cervicalSpine : false },
      { key: 'lumbarSpine', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.lumbarSpine : false },
      { key: 'thoracicSpine', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.thoracicSpine : false }
    ]
    const limGroup3 = [
      { key: 'heartAttack', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.heartAttack : false },
      { key: 'anginaPectoris', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.anginaPectoris : false },
      { key: 'cardiacArrhythmias', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.cardiacArrhythmias : false },
      { key: 'circulatoryDisorders', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.circulatoryDisorders : false },
      { key: 'stroke', isSelected: this.healthLimitationsSet ? this.healthLimitationsSet.stroke : false }
    ]
    const limGroup4 = [{ key: 'low' }, { key: 'normal' }, { key: 'high' }, { key: 'unknown' }] // this is radio buttons group

    const titles = HealthLimitationGroupsTitles
    const limitations = [limGroup1, limGroup2, limGroup3, limGroup4]

    limitations.map((limitation, limIndex) => {
      limitationGroups.push({
        title: titles[limIndex],
        limitations: limitation,
        multipleSelection: limitation[0].hasOwnProperty('isSelected'),
        selectedValue: this.healthLimitationsSet ? this.healthLimitationsSet.bloodPressure : '' // used for the blood pressure radio group only
      })
    })
    this.limitationGroups = limitationGroups

    this.mealFrequency = this.healthLimitationsSet!.mealFrequency
    this.waterConsumption = this.healthLimitationsSet!.waterConsumption
    this.behaviourInfo = this.healthLimitationsSet!.behaviourInfo
  }

  updateLimitationCheckboxValue (item, itemIndex, limitationGroupIndex) {
    this.limitationGroups[limitationGroupIndex].limitations[itemIndex].isSelected = !this.limitationGroups[limitationGroupIndex].limitations[itemIndex].isSelected
    this.healthLimitationsSet[(item.key)] = !this.healthLimitationsSet[item.key]
    this.isUpdated = true
  }

  updateLimitationRadioValue (healthSetKey, limitationGroupIndex, value) {
    if (value === '') return
    this.limitationGroups[limitationGroupIndex].selectedValue = value
    this.healthLimitationsSet[healthSetKey] = value
    this.isUpdated = true
  }

  updateStrings (healthSetKey, value) {
    this[healthSetKey] = value
    this.healthLimitationsSet[healthSetKey] = value
    this.isUpdated = true
  }

  updateArrays (array, item, index) {
    this[array][index].isSelected = !this[array][index].isSelected
    this.healthLimitationsSet[item.key] = !this.healthLimitationsSet[item.key]
    this.isUpdated = true
  }

  updateAsthmaType (type) {
    this.asthma = type
    this.healthLimitationsSet['asthmaBronchiale'] = false
    this.healthLimitationsSet['asthmaAllergic'] = false
    this.healthLimitationsSet['asthmaExertional'] = false
    if (type) {
      this.healthLimitationsSet[type] = true
    }

    this.isUpdated = true
  }

  render (h: CreateElement) {
    if (this.loading) return <Loader />

    let limitationGroups = this.limitationGroups.map((item, index) => {
      return this.renderLimitationGroup(h, item, index)
    })

    return <div class='health-limitations'>
      <Subtitle1 class='title'>{this.$t('HEALTH_LIMITATIONS')}</Subtitle1>

      <ContentBox overflow='scrollable'>
        <div slot='content'>
          {limitationGroups}

          <div class='limitation-group medium'>{this.renderDiseases(h)}</div>

          <div class='limitation-group medium large-on-devices'><Subtitle1>{this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.MEDICATIONS_OPERATIONS')}</Subtitle1>
            <Textarea value={this.medication} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.MEDICATIONS')}
                      keyup={(value) => this.updateStrings('medication', value)} />
            <Textarea value={this.operations} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.OPERATIONS')}
                      keyup={(value) => this.updateStrings('operations', value)} />
          </div>

          {this.renderNutrition(h)}
          {this.renderBehaviour(h)}

          <div class='limitation-group large'>
            <Textarea value={this.notes} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ADDITIONAL_INFO')}
                      keyup={(value) => this.updateStrings('notes', value)} />
          </div>
        </div>

        {this.renderButton(h)}
      </ContentBox>
    </div>
  }

  protected renderNutrition (h: CreateElement) {
    return <div class='limitation-group medium large-on-devices'>
      <Subtitle1 class='row'>{this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.NUTRITION')}</Subtitle1>
      <Select valueChanged={(value: string) => this.updateStrings('waterConsumption', value)} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DAILY_WATER')}
              value={this.waterConsumption}>
        <OptionGroup>
          <Option type='single-option' value={'<1'} label='<1' selected={this.waterConsumption === '<1'}/>
          <Option type='single-option' value={'1'} label='1' selected={this.waterConsumption === '1'}/>
          <Option type='single-option' value={'1.5'} label='1.5' selected={this.waterConsumption === '1.5'}/>
          <Option type='single-option' value={'2'} label='2' selected={this.waterConsumption === '2'}/>
          <Option type='single-option' value={'>2'} label='>2' selected={this.waterConsumption === '>2'}/>
        </OptionGroup>
      </Select>
      <Select valueChanged={(value: string) => this.updateStrings('mealFrequency', value)} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DAILY_MEAL_FREQUENCY')}
              value={this.mealFrequency}>
        <OptionGroup>
          <Option type='single-option' value={'<3'} label='<3' selected={this.mealFrequency === '<3'}/>
          <Option type='single-option' value={'3'} label='3' selected={this.mealFrequency === '3'}/>
          <Option type='single-option' value={'4'} label='4' selected={this.mealFrequency === '4'}/>
          <Option type='single-option' value={'5'} label='5' selected={this.mealFrequency === '5'}/>
          <Option type='single-option' value={'>5'} label='>5' selected={this.mealFrequency === '>5'}/>
        </OptionGroup>
      </Select>
    </div>
  }

  protected renderBehaviour (h: CreateElement) {
    return <div class='limitation-group medium large-on-devices'>
      <Subtitle1 class='row'>{this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.BEHAVIOUR')}</Subtitle1>
      <Textarea value={this.behaviourInfo} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ADITIONAL_BEHAVIOUR')}
                keyup={(value) => this.updateStrings('behaviourInfo', value)}/>
    </div>
  }

  protected renderLimitationGroup (h: CreateElement, limitationGroup: LimitationGroup, limitationGroupIndex: number) {
    let limitations: any[]
    if (limitationGroup.multipleSelection) {
      limitations = limitationGroup.limitations.map((item, index) => {
        return <Checkbox value={item.isSelected} text={this.$t(`PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.${item.key.toUpperCase()}`)}
                         change={() => this.updateLimitationCheckboxValue(item, index, limitationGroupIndex)}
                         class='limitation' />
      })
    } else {
      limitations = this.renderRadioButtonGroup(h, limitationGroup, limitationGroupIndex)
    }

    return <div class='limitation-group small'>
      <Subtitle1>{this.$t(`PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.${limitationGroup.title.toUpperCase()}`)}</Subtitle1>
      {limitations}
    </div>
  }

  protected renderRadioButtonGroup (h: CreateElement, limitationGroup, limitationGroupIndex) {
    return <el-radio-group>
      {limitationGroup.limitations.map(item => {
        return <Radio value={item.key} text={this.$t(`PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.${item.key.toUpperCase()}`)}
                      bindedProp={limitationGroup.selectedValue}
                      change={(value) => this.updateLimitationRadioValue('bloodPressure', limitationGroupIndex, value)} />
      })}
    </el-radio-group>
  }

  protected renderDiseases (h: CreateElement) {
    let diseases = this.diseases.map((item, index) => {
      return <div><Checkbox value={item.isSelected} text={this.$t(`PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.${item.key.toUpperCase()}`)}
                            change={() => this.updateArrays('diseases', item, index)} /></div>
    })

    let pregnancies = this.pregnancy.map((item, index) => {
      return <Checkbox value={item.isSelected} text={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.' + item.key.toUpperCase())}
                       change={() => this.updateArrays('pregnancy', item, index)} class='disease' />
    })

    return <div class='additional-diseases'>
      <Subtitle1 class='row'>{this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ADDITIONAL_DISEASES')}</Subtitle1>

      <div class='row'>
        <Select valueChanged={(value: string) => this.updateStrings('diabetes', value)} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DIABETES')}
                value={(!this.diabetes ? this.$t('NO') : (this.diabetes === 'type1' ? this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DIABETES_TYPE1') : this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DIABETES_TYPE2')))}>
          <OptionGroup>
            <Option type='single-option' value={''} label={this.$t('NO')} selected={this.diabetes === ''} />
            <Option type='single-option' value={'type1'} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DIABETES_TYPE1')} selected={this.diabetes === 'type1'} />
            <Option type='single-option' value={'type2'} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.DIABETES_TYPE2')} selected={this.diabetes === 'type2'} />
          </OptionGroup>
        </Select>

        <Select valueChanged={this.updateAsthmaType}
                label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ASTHMA')}
                value={(this.asthma.length > 0 ? this.$t(`PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.${this.asthma.toUpperCase()}`) : this.$t('NO'))}>
          <OptionGroup>
            <Option type={'single-option'} value={''} label={this.$t('NO')}/>
            <Option type={'single-option'} value={'asthmaBronchiale'} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ASTHMABRONCHIALE')}/>
            <Option type={'single-option'} value={'asthmaAllergic'} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ASTHMAALLERGIC')}/>
            <Option type={'single-option'} value={'asthmaExertional'} label={this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.ASTHMAEXERTIONAL')}/>
          </OptionGroup>
        </Select>
      </div>

      <div class={'diseases-row'}>
        {diseases}
      </div>

      <Subtitle1>{this.$t('PAGE.MEMBERS.STATUS.HEALTH_LIMITATIONS.PREGNANCY_DETAILS')}</Subtitle1>
      <div class='column pregnancy'>
        {pregnancies}
      </div>
    </div>
  }

  protected renderButton (h: CreateElement) {
    return <div class='btn-row' slot='bottom'>
      <Button ref='saveBtn' label={this.$t('APPLY')} click={this.addHealthLimitationSet} type='elevated' />
    </div>
  }

  protected load (): Promise<void> | undefined {
    return this.loadLatestHealthLimitations()
  }

  protected loadLatestHealthLimitations () {
    return new HealthLimitationService().loadLatestHealthLimitations(this.$route.params.id)
      .then((latestSet) => {
        this.healthLimitationsSet = new HealthLimitationSet(camelcaseKeys(latestSet))
        this.loading = false
      })
      .finally(() => this.assignHealthLimResponseToData())
  }

  protected addHealthLimitationSet () {
    if (this.isUpdated) {
      return new HealthLimitationService().addHealthLimitationSet(this.healthLimitationsSet, this.$route.params.id)
        .then((responseSet) => {
          this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
          this.healthLimitationsSet = responseSet
          this.isUpdated = false
        })
    }
  }

}
